import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  TablePagination,
  IconButton,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  DialogTitle,
  Tooltip,
  ImageList,
  ImageListItem
} from '@mui/material';
import { collection, query, orderBy, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { ref as storageRef, deleteObject } from 'firebase/storage';
import { db, storage } from '../config/firebase';
import ImageIcon from '@mui/icons-material/Image';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import logo from './equitas-oht-logo.png';

const theme = {
  primary: '#EF582E',
  secondary: '#F17F5E',
  background: '#FFF9F5',
  text: '#2D3748',
  fontFamily: '"Plus Jakarta Sans", sans-serif',
};

const ReportsTable = () => {
  const [reports, setReports] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedImage, setSelectedImage] = useState(null);
  const [sortBy, setSortBy] = useState('timestamp');
  const [order, setOrder] = useState('desc');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [reportToDelete, setReportToDelete] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  useEffect(() => {
    const fetchReports = async () => {
      const q = query(collection(db, 'reports'), orderBy('timestamp', 'desc'));
      const querySnapshot = await getDocs(q);
      const fetchedReports = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate()
      }));
      setReports(fetchedReports);
    };

    fetchReports();
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDate = (date) => {
    if (!date) return '-';
    const d = new Date(date);
    return d.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const handleRequestSort = (property) => {
    const isAsc = sortBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setSortBy(property);
    setPage(0);
  };

  const compareValues = (a, b) => {
    if (!a && !b) return 0;
    if (!a) return 1;
    if (!b) return -1;

    if (a instanceof Date && b instanceof Date) {
      return a - b;
    }

    return a.toString().localeCompare(b.toString());
  };

  const sortedReports = React.useMemo(() => {
    return [...reports].sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];

      // Convertir les timestamps Firestore en Date si nécessaire
      if (sortBy === 'timestamp' || sortBy === 'date') {
        aValue = aValue instanceof Date ? aValue : new Date(aValue);
        bValue = bValue instanceof Date ? bValue : new Date(bValue);
      }
      
      return order === 'asc' 
        ? compareValues(aValue, bValue)
        : compareValues(bValue, aValue);
    });
  }, [reports, order, sortBy]);

  const SortableTableCell = ({ id, label }) => {
    const isSortable = ['timestamp', 'date', 'platform'].includes(id);
    
    return (
      <TableCell
        sx={{
          fontWeight: 'bold',
          fontFamily: theme.fontFamily,
          cursor: isSortable ? 'pointer' : 'default',
          userSelect: 'none',
          '&:hover': isSortable ? {
            color: theme.primary
          } : {}
        }}
        onClick={() => isSortable && handleRequestSort(id)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {label}
          {isSortable && (
            <Box component="span" sx={{ ml: 1, color: theme.primary }}>
              {sortBy === id ? (order === 'asc' ? '↑' : '↓') : '↕'}
            </Box>
          )}
        </Box>
      </TableCell>
    );
  };

  const truncateText = (text, maxLength = 100) => {
    if (!text) return '-';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Fonction pour ouvrir la confirmation de suppression
  const handleDeleteClick = (report) => {
    setReportToDelete(report);
    setDeleteConfirmOpen(true);
  };

  // Fonction pour supprimer le signalement
  const handleDeleteConfirm = async () => {
    try {
      // Supprimer les fichiers dans Storage
      if (reportToDelete.attachmentUrls?.length > 0) {
        for (const url of reportToDelete.attachmentUrls) {
          try {
            // Extraire le chemin du fichier de l'URL
            const fileRef = storageRef(storage, url);
            await deleteObject(fileRef);
          } catch (error) {
            console.error("Erreur lors de la suppression du fichier:", error);
          }
        }
      }

      // Supprimer le document dans Firestore
      await deleteDoc(doc(db, 'reports', reportToDelete.id));
      
      // Mettre à jour l'état local
      setReports(reports.filter(report => report.id !== reportToDelete.id));
      setDeleteConfirmOpen(false);
      setReportToDelete(null);
    } catch (error) {
      console.error("Erreur lors de la suppression:", error);
    }
  };

  const handleOpenDetails = (report) => {
    setSelectedReport(report);
    setDetailsOpen(true);
  };

  return (
    <Box sx={{ 
      width: '100%',
      mx: 'auto', 
      p: 3, 
      mt: 4,
      fontFamily: theme.fontFamily,
      letterSpacing: '-0.3px'
    }}>
      <Box sx={{ 
        display: 'flex',
        justifyContent: 'center',
        mb: 4
      }}>
        <img 
          src={logo} 
          alt="Equitas Logo" 
          style={{ 
            height: '40px',
            width: 'auto'
          }} 
        />
      </Box>
      <Paper 
        elevation={0}
        sx={{ 
          borderRadius: 2,
          backgroundColor: theme.background,
          border: `1px solid ${theme.primary}40`
        }}
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <SortableTableCell 
                  id="timestamp" 
                  label="Date du signalement"
                  sx={{ width: '12%' }}
                />
                <SortableTableCell 
                  id="date" 
                  label="Date du propos"
                  sx={{ width: '12%' }}
                />
                <SortableTableCell 
                  id="platform" 
                  label="Plateforme"
                  sx={{ width: '15%' }}
                />
                <TableCell 
                  sx={{ 
                    fontWeight: 'bold', 
                    fontFamily: theme.fontFamily,
                    width: '40%'
                  }}
                >
                  Description
                </TableCell>
                <TableCell 
                  sx={{ 
                    fontWeight: 'bold', 
                    fontFamily: theme.fontFamily,
                    width: '10%'
                  }}
                >
                  Lien
                </TableCell>
                <TableCell 
                  sx={{ 
                    fontWeight: 'bold', 
                    fontFamily: theme.fontFamily,
                    width: '11%'
                  }}
                >
                  Capture(s) d'écran
                </TableCell>
                <TableCell 
                  sx={{ 
                    fontWeight: 'bold', 
                    fontFamily: theme.fontFamily,
                    width: '5%'
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedReports
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((report) => (
                  <TableRow key={report.id}>
                    <TableCell sx={{ fontFamily: theme.fontFamily }}>
                      {formatDate(report.timestamp)}
                    </TableCell>
                    <TableCell sx={{ 
                      fontFamily: theme.fontFamily,
                      fontWeight: 'bold'
                    }}>
                      {formatDate(report.date)}
                    </TableCell>
                    <TableCell sx={{ fontFamily: theme.fontFamily }}>
                      {report.platform === 'other' ? report.otherPlatform : report.platform}
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        fontFamily: theme.fontFamily,
                        maxWidth: '300px',
                        whiteSpace: 'normal',
                        wordWrap: 'break-word'
                      }}
                    >
                      {truncateText(report.comments)}
                    </TableCell>
                    <TableCell>
                      {report.link ? (
                        <IconButton
                          href={report.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ color: theme.primary }}
                        >
                          <OpenInNewIcon />
                        </IconButton>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      {report.attachmentUrls?.length > 0 ? (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {report.attachmentUrls.map((url, index) => (
                            <IconButton
                              key={index}
                              onClick={() => setSelectedImage(url)}
                              sx={{ color: theme.primary }}
                            >
                              <ImageIcon />
                            </IconButton>
                          ))}
                        </Box>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Voir les détails">
                          <IconButton
                            onClick={() => handleOpenDetails(report)}
                            sx={{ color: theme.primary }}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Supprimer">
                          <IconButton
                            onClick={() => handleDeleteClick(report)}
                            sx={{ 
                              color: theme.primary,
                              '&:hover': {
                                color: '#d32f2f'
                              }
                            }}
                          >
                            <DeleteOutlineIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={sortedReports.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{ 
            fontFamily: theme.fontFamily,
            '.MuiTablePagination-select': {
              fontFamily: theme.fontFamily
            }
          }}
        />
      </Paper>

      <Dialog
        open={!!selectedImage}
        onClose={() => setSelectedImage(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogContent sx={{ p: 0 }}>
          {selectedImage && (
            <img
              src={selectedImage}
              alt="Capture d'écran"
              style={{ width: '100%', height: 'auto' }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setSelectedImage(null)}
            sx={{ 
              color: theme.primary,
              fontFamily: theme.fontFamily
            }}
          >
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de confirmation de suppression */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle sx={{ fontFamily: theme.fontFamily }}>
          Confirmer la suppression
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: theme.fontFamily }}>
            Êtes-vous sûr de vouloir supprimer ce signalement ?
            Cette action est irréversible.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteConfirmOpen(false)}
            sx={{ 
              color: theme.text,
              fontFamily: theme.fontFamily
            }}
          >
            Annuler
          </Button>
          <Button 
            onClick={handleDeleteConfirm}
            sx={{ 
              color: '#d32f2f',
              fontFamily: theme.fontFamily,
              '&:hover': {
                bgcolor: 'rgba(211, 47, 47, 0.04)'
              }
            }}
          >
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog des détails du signalement */}
      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ 
          fontFamily: theme.fontFamily,
          borderBottom: `1px solid ${theme.primary}40`,
          mb: 2
        }}>
          Détails du signalement
        </DialogTitle>
        <DialogContent>
          {selectedReport && (
            <Box sx={{ 
              display: 'grid', 
              gap: 2,
              fontFamily: theme.fontFamily
            }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Date du signalement
                </Typography>
                <Typography>
                  {formatDate(selectedReport.timestamp)}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Date du propos
                </Typography>
                <Typography sx={{ fontWeight: 'bold' }}>
                  {formatDate(selectedReport.date)}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Nom
                </Typography>
                <Typography>
                  {selectedReport.name || '-'}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Email
                </Typography>
                <Typography>
                  {selectedReport.email || '-'}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Type
                </Typography>
                <Typography>
                  {selectedReport.isPublic === 'public' ? 'Public' : 'Message privé'}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Plateforme
                </Typography>
                <Typography>
                  {selectedReport.platform === 'other' 
                    ? selectedReport.otherPlatform 
                    : selectedReport.platform}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Lien
                </Typography>
                {selectedReport.link ? (
                  <Button
                    href={selectedReport.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ 
                      color: theme.primary,
                      p: 0,
                      textTransform: 'none',
                      fontFamily: theme.fontFamily
                    }}
                  >
                    {selectedReport.link}
                  </Button>
                ) : (
                  <Typography>-</Typography>
                )}
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Description
                </Typography>
                <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                  {selectedReport.comments || '-'}
                </Typography>
              </Box>

              {selectedReport.attachmentUrls?.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Captures d'écran
                  </Typography>
                  <ImageList sx={{ width: '100%' }} cols={2} rowHeight={200}>
                    {selectedReport.attachmentUrls.map((url, index) => (
                      <ImageListItem 
                        key={index}
                        sx={{ cursor: 'pointer' }}
                        onClick={() => setSelectedImage(url)}
                      >
                        <img
                          src={url}
                          alt={`Capture d'écran ${index + 1}`}
                          loading="lazy"
                          style={{ 
                            height: '200px',
                            objectFit: 'cover'
                          }}
                        />
                      </ImageListItem>
                    ))}
                  </ImageList>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDetailsOpen(false)}
            sx={{ 
              color: theme.primary,
              fontFamily: theme.fontFamily
            }}
          >
            Fermer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReportsTable; 